import React from 'react';
import { Drawer, Card, Grid } from '@mui/material';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import ReactMarkdown, { Components } from 'react-markdown';
import MultiStar from '../../atoms/Icons/MultiStar';
import { Close } from '@HomaHealth/ui-component-library';

interface AiSummaryItem {
  title: string;
  description: string;
}

interface SummaryCardProps {
  open: boolean;
  onClose: () => void;
  aiSummaryData?: AiSummaryItem[];
  loading?: boolean;
}

/* ✨ Shimmer animation styles */
const shimmerStyle = `
@keyframes shimmer {
  0% {
    background-position: -500px 0;
  }
  100% {
    background-position: 500px 0;
  }
}

.shimmer {
  background: linear-gradient(
    90deg,
    #e5e9f0 25%,
    #f8fafc 50%,
    #e5e9f0 75%
  );
  background-size: 800px 100%;
  animation: shimmer 1.8s infinite linear;
}
`;

const markdownComponents: Components = {
  ul: ({ children, ...props }) => (
    <ul
      style={{
        paddingLeft: '18px',
        margin: '4px 0',
        listStyleType: 'disc',
      }}
      {...props}
    >
      {children}
    </ul>
  ),
  li: ({ children, ...props }) => (
    <li
      style={{
        marginBottom: '5px',
        color: '#444',
        fontSize: '13px',
      }}
      {...props}
    >
      {children}
    </li>
  ),
  strong: ({ children, ...props }) => (
    <strong
      style={{
        color: '#14326D',
        fontWeight: 600,
      }}
      {...props}
    >
      {children}
    </strong>
  ),
  p: ({ children, ...props }) => (
    <p style={{ margin: '2px 0', fontSize: '13px' }} {...props}>
      {children}
    </p>
  ),
};

const AiSummaryCard: React.FC<SummaryCardProps> = ({
  open,
  onClose,
  aiSummaryData,
  loading,
}) => {
  return (
    <>
      {/* Inject shimmer animation */}
      <style>{shimmerStyle}</style>

      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        ModalProps={{
          disableScrollLock: true, // allow background scroll
          slotProps: {
            backdrop: {
              style: {
                backgroundColor: 'rgba(0,0,0,0.1)', // optional overlay
              },
            },
          },
        }}
        slotProps={{
          paper: {
            style: {
              width: 520,
              top: 120,
              height: 'calc(100% - 120px)',
              borderTopLeftRadius: 20,
              overflow: 'hidden',
            },
          },
        }}
      >
        <Card
          style={{
            height: '100%',
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'none',
          }}
        >
          {/* ── Header ── */}
          <Grid
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 16px',
              width: '100%',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              fontFamily="Inter"
              fontSize="16px"
              lineHeight="160%"
              styleProps={{
                fontWeight: 500,
                background:
                  'linear-gradient(90deg, #14326D 0%, #1492CC 50%, #1A4C6D 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: 'flex',
                // justifyContent: 'center',
                alignItems: 'center',
                gap: 7,
              }}
            >
              <MultiStar />
              Tell Me More
            </Typography>
            <Close fill="#B0B7C3" onClick={onClose} cursor={'pointer'} />
          </Grid>

          {/* ── Gradient Divider ── */}
          <hr
            style={{
              border: 'none',
              height: '2px',
              width: '100%',
              margin: 0,
              background:
                'linear-gradient(90deg, #14326D 0%, #1492CC 50%, #1A4C6D 100%)',
            }}
          />

          {/* ── Scrollable Content ── */}
          <Grid
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {/* ── Loading State ── */}
            {loading && (
              <Grid
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                }}
              >
                {[1, 2, 3].map((i) => (
                  <Grid
                    key={i}
                    className="shimmer"
                    style={{
                      padding: '16px 20px',
                      borderTopLeftRadius: '25px',
                      borderBottomLeftRadius: '25px',
                      borderBottomRightRadius: '25px',
                    }}
                  >
                    <div
                      className="shimmer"
                      style={{
                        height: '14px',
                        width: '60%',
                        borderRadius: '6px',
                        marginBottom: '10px',
                      }}
                    />
                    <div
                      className="shimmer"
                      style={{
                        height: '10px',
                        width: '90%',
                        borderRadius: '6px',
                        marginBottom: '6px',
                      }}
                    />
                    <div
                      className="shimmer"
                      style={{
                        height: '10px',
                        width: '75%',
                        borderRadius: '6px',
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ── Empty State ── */}
            {!loading && (!aiSummaryData || aiSummaryData.length === 0) && (
              <Grid
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#888',
                  fontFamily: 'Inter',
                  fontSize: '14px',
                }}
              >
                No summary available.
              </Grid>
            )}

            {/* ── Summary Cards ── */}
            {!loading &&
              aiSummaryData &&
              aiSummaryData.length > 0 &&
              aiSummaryData.map((item, index) => (
                <Grid
                  key={index}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    padding: '16px 20px',
                    background: '#E5E9F0',
                    borderTopLeftRadius: '25px',
                    borderBottomLeftRadius: '25px',
                    borderBottomRightRadius: '25px',
                  }}
                >
                  {/* Card Title */}
                  <Typography
                    fontFamily="Inter"
                    fontSize="14px"
                    lineHeight="160%"
                    styleProps={{
                      fontWeight: 700,
                      background:
                        'linear-gradient(90deg, #14326D 0%, #1492CC 50%, #1A4C6D 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      display: 'inline-block',
                    }}
                  >
                    {item.title}
                  </Typography>

                  {/* Divider under title */}
                  <hr
                    style={{
                      border: 'none',
                      height: '1px',
                      background: '#c8d0de',
                      margin: '2px 0 6px 0',
                    }}
                  />

                  {/* Markdown Description */}
                  <div
                    style={{
                      fontSize: '13px',
                      fontFamily: 'Inter',
                      color: '#444',
                      lineHeight: '1.7',
                    }}
                  >
                    <ReactMarkdown components={markdownComponents}>
                      {item.description}
                    </ReactMarkdown>
                  </div>
                </Grid>
              ))}
          </Grid>
        </Card>
      </Drawer>
    </>
  );
};

export default AiSummaryCard;
